import Modal from '@/ctComponents/Modal';
import FileUpload from '@/components/FileUpload';
import { Input } from '@/components/Input';
import React, { useEffect, useMemo, useState } from 'react';
import { Icons } from '@/assets';
import { createPortal } from 'react-dom';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import * as yup from 'yup';
import {
  createHelpCenterVideo,
  editHelpCenterVideo,
  SaveApiResMessage,
} from '@/actions';
import { BASE_URL } from '@/actions/API';

const formSchema = yup.object().shape({
  title: yup.string().required('Title is required'),
  tags: yup.string().required('Tags are required'),
  order_number: yup.string(),
  description: yup.string().required('Description is required'),
  video: yup.string().required('Video file is required'),
  status: yup.object().shape({
    label: yup.string().required('Video status label is required'),
    value: yup.string().required('Video status value is required'),
  }),
});

export const UploadVideo = ({ show, setShow, data, refetchList }) => {
  const [uploadedFile, setUploadedFile] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    tags: [],
    order_number: '',
    description: '',
    video: '',
    status: {
      label: 'Hidden',
      value: 'HIDDEN',
    },
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEdit = useMemo(() => (data ? true : false), [data]);

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || '',
        tags: data.tags || [],
        order_number: data.order_number || '',
        description: data.description || '',
        video: BASE_URL.replace('/api', '') + data.url || '',
        status: {
          label: data.status === 'ACTIVE' ? 'Active' : 'Hidden',
          value: data.status || 'ACTIVE',
        },
      });
      setUploadedFile([
        {
          name: `${data.title}.${data.url.split('.').pop()}`,
          size: 0,
          type: 'video/mp4',
        },
      ]);
    } else {
      setFormData({
        title: '',
        tags: [],
        order_number: '',
        description: '',
        video: '',
        status: {
          label: 'Hidden',
          value: 'HIDDEN',
        },
      });
      setUploadedFile([]);
      setIsSubmitting(false);
    }
  }, [data]);

  const handleFilesChange = (files) => {
    setUploadedFile(files);
    setFormData((prev) => ({
      ...prev,
      video: files[0].file,
    }));
  };

  const handleRemoveFile = (file) => {
    setUploadedFile([]);
    setFormData((prev) => ({
      ...prev,
      video: '',
    }));
  };

  const validateForm = async () => {
    try {
      await formSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error) {
      const validationErrors = {};
      error.inner.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await validateForm();
    if (!isValid) {
      SaveApiResMessage({
        title: 'Error',
        message: 'Please fill all the required fields',
        mode: 'error',
      });
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    if (isEdit) {
      editHelpCenterVideo(data.uuid, {
        ...formData,
        status: formData.status.value,
        video:
          typeof formData.video === 'string' &&
          formData.video.includes(data.url)
            ? null
            : formData.video,
      })
        .then((res) => {
          refetchList();
          setShow(false);
          SaveApiResMessage({
            title: 'Success',
            message: 'Video updated successfully',
            mode: 'success',
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      createHelpCenterVideo({
        ...formData,
        status: formData.status.value,
      })
        .then((res) => {
          refetchList();
          setShow(false);
          SaveApiResMessage({
            title: 'Success',
            message: 'Video created successfully',
            mode: 'success',
          });
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return createPortal(
    <Modal
      show={show}
      setShow={setShow}
      icon="videoPlay"
      title={isEdit ? 'Edit Video' : 'Video Upload'}
      submitText="Submit"
      submit={handleSubmit}
      loadingbutton={isSubmitting}
      titleColor="#131517"
      minWidth={'unset'}
      maxHeight={700}
      buttonPosition="end"
      children={
        <div className="w-full space-y-4 min-w-360 md:space-y-0 md:grid grid-cols-1 md:grid-cols-2 gap-2 pt-5 md:min-w-640 max-w-795 mx-auto">
          <Input
            label=" Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Enter title"
            required
            error={errors.title}
          />
          <Input
            label="Tags"
            value={formData.tags}
            onChange={(e) => handleChange('tags', e.target.value)}
            placeholder="Enter tags separated by commas: Tag1,Tag2,Tag3"
            required
            error={errors.tags}
          />
          <Input
            label="Displayed Number"
            value={formData.order_number}
            onChange={(e) => handleChange('order_number', e.target.value)}
            placeholder="Enter displayed number"
          />

          <div className="col-span-2">
            <Input
              label=" Description"
              required
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter description"
              error={errors.description}
              errorMessage={errors.description}
              variant="textarea"
            />
          </div>

          <div className="col-span-2">
            <p className="text-sm font-bold text-primary-text m-0 mb-2">
              Upload a video file here
            </p>
            <FileUpload
              title="Drag & Drop your files"
              acceptedFileTypes=".mp4,.webm,.ogg"
              fileTypeDescription="Only support MP4, WebM, or OGG (max. 100MB)"
              maxFileSize={200000000}
              maxFiles={1}
              onFilesChange={handleFilesChange}
              uploadedFiles={uploadedFile}
              defaultFileUrl={formData.video || ''}
              onRemoveFile={handleRemoveFile}
              uploadIcon={Icons.imageUpload}
              iconWidth={214}
              iconHeight={133}
              showFilePreview={false}
              dragIndicatorText="Drop here"
              browseButtonText="Browse files"
              allowMultiple={false}
            />
          </div>
          {uploadedFile.length > 0 && (
            <CustomSelectEx
              dropdown
              height={48}
              maxHeight={48}
              title="Video Status"
              options={[
                { label: 'Hidden', value: 'HIDDEN' },
                { label: 'Active', value: 'ACTIVE' },
              ]}
              value={formData.status}
              onChange={(e) => {
                handleChange('status', {
                  label: e.label,
                  value: e.value,
                });
              }}
            />
          )}
        </div>
      }
    />,
    document.body
  );
};
